import { LegalPage } from "../components/LegalPage";
import { CONTACT_EMAIL } from "../lib/contact";

/** Terms of Service (`/terms`). See `LegalPage` for shared chrome. */
export function Terms() {
  return (
    <LegalPage title="Terms of Service">
      <p>
        These Terms of Service ("Terms") govern your access to and use of FaB AI
        Judge (the "Service"). The Service is operated by an independent
        individual ("Operator", "we", "us", or "our").
      </p>
      <p>
        By accessing or using the Service, you agree to these Terms. If you do
        not agree, do not use the Service.
      </p>

      <h2>1. Description of the Service</h2>
      <p>
        FaB AI Judge is an experimental, AI-powered rules assistant for the
        Flesh and Blood Trading Card Game. It generates answers using large
        language models and a dataset containing game rules, card data, and
        related information.
      </p>
      <p>
        The Service is provided for informational and educational purposes. Its
        answers may be incorrect, incomplete, outdated, inconsistent, or
        misleading. The Service may misunderstand questions, misinterpret rules
        interactions, or generate information that is not supported by official
        sources.
      </p>
      <p>
        <strong>
          The Service is not an official rules source, and its answers are not
          official rulings.
        </strong>{" "}
        It is not a substitute for official documentation or a qualified human
        judge. For tournament decisions, competitive play, or disputes, consult
        the applicable official rules and a qualified human judge.
      </p>
      <p>
        <strong>
          FaB Judge is in no way affiliated with Legend Story Studios. Legend
          Story Studios®, Flesh and Blood™, and set names are trademarks of
          Legend Story Studios. Flesh and Blood characters, cards, logos, and
          art are property of Legend Story Studios.
        </strong>
      </p>
      <p>
        We do not guarantee that the Service will identify the most recent
        rules, card errata, rulings, or tournament policies.
      </p>

      <h2>2. Eligibility and accounts</h2>
      <p>
        The Service is intended for adults. You must be at least 18 years old to
        use it. Submitted content is not moderated, so the Service is not
        offered to minors.
      </p>
      <p>
        You may use the Service only if you are legally capable of agreeing to
        these Terms and your use is permitted under the laws applicable to you.
        If you are not legally permitted to use the Service, you must not use
        it.
      </p>
      <p>
        Authentication is provided through Google. Your use of Google
        authentication is also subject to Google's applicable terms and privacy
        policies.
      </p>
      <p>
        You are responsible for maintaining the security of your Google account
        and for activity conducted through your account, to the extent permitted
        by applicable law. You must not impersonate another person or
        misrepresent your identity.
      </p>
      <p>
        Registration is open, but the Operator may cap the total number of
        accounts or decline a sign-up at any time, including while the Service is
        in a limited beta. Such a cap does not affect an account that has already
        been created.
      </p>
      <p>
        The Service does not currently provide a self-service account deletion
        feature. Contacting the Operator about an account does not guarantee
        that account-associated information or previously submitted content can
        be deleted. Any applicable legal rights remain unaffected.
      </p>

      <h2>3. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          Use the Service for unlawful purposes or in violation of another
          person's rights.
        </li>
        <li>
          Attempt to bypass usage limits, rate limits, authentication, or other
          technical controls.
        </li>
        <li>
          Scrape, crawl, or bulk-extract content, or automate queries beyond
          ordinary personal use without our authorization.
        </li>
        <li>
          Probe, scan, or test the vulnerability of the Service, or attempt
          unauthorized access to systems, accounts, or data.
        </li>
        <li>
          Submit content that is unlawful, threatening, harassing, or otherwise
          prohibited by applicable law, or content you do not have the right to
          submit.
        </li>
        <li>
          Submit confidential information, sensitive personal information,
          credentials, payment details, or other information that you do not
          want made public.
        </li>
        <li>
          Use the Service or its outputs to develop or train a competing product
          or model, to the extent permitted by applicable law.
        </li>
        <li>
          Resell, sublicense, or commercially redistribute access to the Service
          without our authorization.
        </li>
        <li>
          Interfere with or disrupt the Service, its infrastructure, or its
          providers.
        </li>
        <li>
          Use the Service in a way that infringes intellectual property rights
          or violates applicable law.
        </li>
      </ul>
      <p>
        We may restrict, suspend, or terminate access if we reasonably believe
        that a user has violated these Terms, compromised the Service, or
        created a legal, security, or operational risk.
      </p>
      <p>
        Where reasonably practicable, we may provide notice before taking
        action. We are not required to provide advance notice when immediate
        action is reasonably necessary to protect the Service, its users, or
        third parties.
      </p>

      <h2>4. Public submissions and content license</h2>

      <h3>4.1 Public exposure of submissions</h3>
      <p>
        <strong>
          Parts of what you submit may become visible to other users. Do not
          submit anything you expect to remain private or confidential.
        </strong>
      </p>
      <p>
        Standalone first questions and their answers may be stored in a shared
        answer cache and shown to other users who ask similar questions, without
        attribution to your account. Conversations you share through a public
        share link are accessible to anyone who obtains the link (see section
        5). Content exposed in these ways may be copied, indexed, or
        redistributed by third parties outside our control.
      </p>
      <p>
        In particular, do not include personal contact details, credentials,
        private communications, confidential business information, or sensitive
        personal information in a question.
      </p>
      <p>
        The Service is designed for game-rules questions, not private
        discussions. You should assume that anything you submit may remain
        publicly available indefinitely once it is cached or shared.
      </p>

      <h3>4.2 Rights in submitted content</h3>
      <p>
        You retain whatever rights you hold in content you submit, subject to
        the rights of third parties and applicable law.
      </p>
      <p>
        You grant the Operator a worldwide, non-exclusive, royalty-free license
        to host, store, reproduce, process, publish, display, distribute, and
        adapt submitted content for operating, maintaining, securing, and
        improving the Service, including generating answers, maintaining the
        shared answer cache, and displaying public questions and answers.
      </p>
      <p>
        This license is intended to support the operation of the Service and its
        public cache. It does not transfer ownership of your original content to
        the Operator.
      </p>
      <p>
        The license continues for as long as reasonably necessary to fulfill
        these purposes, subject to applicable law and any mandatory rights you
        may have. Public content may remain available after you stop using the
        Service.
      </p>
      <p>
        You must have the rights and permissions necessary to submit the content
        and grant this license. You must not submit material that infringes
        another person's intellectual property or other rights.
      </p>
      <p>
        Nothing in this section grants you rights to third-party content that
        appears in the Service.
      </p>

      <h2>5. Public sharing and answer cache</h2>

      <h3>5.1 Public sharing</h3>
      <p>
        The Service may provide links that allow conversations or other content
        to be accessed by others.
      </p>
      <p>
        Shared content is accessible to anyone who obtains the relevant link,
        and it may be copied or redistributed independently. A shared link
        should not be treated as private or access-controlled.
      </p>

      <h3>5.2 Shared answer cache</h3>
      <p>
        The Service may store questions and answers in a shared cache to reduce
        processing costs and response times. Only standalone first questions are
        cached; follow-up questions within a conversation are not. When a later
        user submits a sufficiently similar question, the Service may reuse a
        cached answer instead of generating a new one.
      </p>
      <p>
        Cached content may be reviewed by the Operator for quality assurance,
        troubleshooting, maintenance, and improvement of the Service.
      </p>
      <p>
        The cache is intended to be retained indefinitely. The Service does not
        currently provide a user-facing mechanism to delete individual cached
        questions or answers. Removing a conversation from a user interface, if
        such a feature is available, does not necessarily remove cached content,
        copies, backups, or material previously accessed by third parties.
      </p>
      <p>
        We do not guarantee that public content can be removed from search
        engines, third-party archives, or copies made by other users.
      </p>
      <p>
        These retention practices are subject to applicable law. Nothing in
        these Terms excludes any mandatory legal rights that may apply to your
        information.
      </p>
      <p>
        See our <a href="/privacy">Privacy Policy</a> for further information
        about personal information and data processing.
      </p>

      <h2>6. AI-generated output and reliance</h2>
      <p>
        All output is generated automatically and is provided for general
        informational purposes only.
      </p>
      <p>
        The Service may produce inaccurate explanations, misapply rules, omit
        relevant exceptions, or present a conclusion confidently despite being
        wrong. Similar questions may produce different answers, and cached
        answers may not reflect later changes to the rules or card data.
      </p>
      <p>
        You are responsible for independently verifying output before relying on
        it. You must not treat an answer as an official ruling or rely on it as
        the sole basis for a tournament decision.
      </p>
      <p>
        The Service does not provide legal, financial, medical, or other
        professional advice.
      </p>
      <p>
        To the maximum extent permitted by applicable law, the Operator
        disclaims responsibility for decisions you make based on AI-generated
        output, subject to the liability provisions below and any liability that
        cannot legally be excluded.
      </p>

      <h2>7. Free usage</h2>
      <p>
        The Service is free. Each account gets a limited number of
        model-generated answers within a rolling window (currently 24 hours).
        The amount, availability, and reset period may change at our discretion.
        We may impose or adjust usage limits to manage costs, prevent abuse, or
        maintain service availability.
      </p>
      <p>
        When the allowance is spent, model-generated answers are unavailable
        until the window rolls over. Answers already in the shared cache remain
        available in the meantime.
      </p>
      <p>Free usage has no monetary value and is not redeemable for cash.</p>

      <h3>7.1 Voluntary support</h3>
      <p>
        You may optionally support the project through a third-party platform
        such as Ko-fi. Support is a voluntary contribution. It does not
        purchase, unlock, extend, or replenish any usage allowance, and it
        confers no additional features, priority, or other entitlement.
      </p>
      <p>
        Any payment you make through such a platform is a transaction between
        you and that platform, governed by its terms and privacy policy. We may
        stop accepting support at any time.
      </p>

      <h2>8. Availability, changes, and termination</h2>
      <p>
        The Service is provided on an "as available" basis. We do not guarantee
        uninterrupted availability, compatibility with every device, or
        continued operation of any particular feature.
      </p>
      <p>
        We may modify, suspend, or discontinue all or part of the Service,
        including the AI models, datasets, cache, or free allowances.
      </p>
      <p>
        We may suspend or terminate your access for violations of these Terms,
        security concerns, abuse, legal requirements, or operational reasons.
      </p>
      <p>
        You may stop using the Service at any time. Stopping use does not
        automatically delete previously submitted content, cached questions and
        answers, or copies held by third parties.
      </p>
      <p>
        Where applicable law requires notice, refunds, data access, deletion, or
        other measures following a change or termination, we will comply with
        those requirements.
      </p>

      <h2>9. Third-party services and data sources</h2>
      <p>
        The Service relies on third-party services, which may include Google
        authentication, hosting providers, databases, AI model providers, and
        payment processors.
      </p>
      <p>
        Those providers may have their own terms, privacy policies, and
        operational limitations. We do not control all aspects of their
        services.
      </p>
      <p>
        The Service may use game rules, card data, and other material associated
        with Flesh and Blood. Such material may be subject to third-party
        intellectual property rights and applicable usage restrictions.
      </p>
      <p>
        The presence of third-party material in the Service does not imply
        endorsement by its rights holders or grant users permission to reproduce
        or redistribute that material independently.
      </p>
      <p>
        Links to external websites are provided for convenience. We are not
        responsible for the availability or content of external websites,
        subject to applicable law.
      </p>

      <h2>10. Disclaimers of warranties</h2>
      <p>
        <strong>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE SERVICE AND ALL
          OUTPUT ARE PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT WARRANTIES OF
          ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
        </strong>
      </p>
      <p>
        <strong>
          WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE,
          ERROR-FREE, OR AVAILABLE AT ANY PARTICULAR TIME. WE DO NOT WARRANT
          THAT ANY OUTPUT WILL BE ACCURATE, COMPLETE, CURRENT, CONSISTENT, OR
          SUITABLE FOR YOUR PURPOSE.
        </strong>
      </p>
      <p>
        <strong>
          WE DO NOT WARRANT THAT THE SERVICE WILL REFLECT ALL OFFICIAL RULES,
          CARD ERRATA, FORMATS, OR RULINGS.
        </strong>
      </p>
      <p>
        Nothing in these Terms excludes a warranty, guarantee, or other right
        that cannot lawfully be excluded under applicable law.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE OPERATOR WILL NOT
        BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
        DAMAGES, OR FOR LOSS OF PROFITS, REVENUE, BUSINESS OPPORTUNITY, DATA, OR
        GOODWILL, ARISING OUT OF OR RELATING TO YOUR ACCESS TO OR USE OF THE
        SERVICE.
      </p>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE OPERATOR WILL NOT
        BE LIABLE FOR LOSSES ARISING FROM RELIANCE ON INACCURATE, INCOMPLETE,
        OUTDATED, OR OTHERWISE INCORRECT AI-GENERATED OUTPUT.
      </p>
      <p>
        Nothing in these Terms excludes or limits liability where such exclusion
        or limitation is prohibited by law, including any liability that cannot
        lawfully be excluded for fraud, intentional misconduct, or other
        non-excludable conduct.
      </p>
      <p>
        These limitations apply regardless of the legal theory under which a
        claim is brought, to the extent permitted by applicable law.
      </p>

      <h2>12. Indemnification</h2>
      <p>
        To the extent permitted by applicable law, you agree to indemnify the
        Operator against third-party claims, losses, and reasonable legal
        expenses to the extent directly arising from:
      </p>
      <ul>
        <li>
          Content you submit that infringes a third party's rights or violates
          applicable law.
        </li>
        <li>Your unlawful use of the Service.</li>
        <li>Your material violation of these Terms.</li>
      </ul>
      <p>
        This obligation does not apply to the extent a claim results from the
        Operator's own unlawful conduct, negligence, or breach of a
        non-excludable legal obligation.
      </p>
      <p>
        The Operator will not impose an indemnification obligation to the extent
        prohibited by applicable law.
      </p>

      <h2>13. Changes to these Terms</h2>
      <p>
        We may update these Terms to reflect changes to the Service, its
        operation, applicable law, or our business practices.
      </p>
      <p>
        When we make changes, we will update the "Last updated" date above.
        Where required by law, we will provide advance notice or obtain renewed
        acceptance before material changes take effect.
      </p>
      <p>
        For changes that do not require renewed acceptance, continued use of the
        Service after the effective date may constitute acceptance of the
        revised Terms, to the extent permitted by applicable law.
      </p>
      <p>
        If you do not agree to a revised version, you must stop using the
        Service.
      </p>
      <p>
        Changes will not retroactively remove rights that cannot lawfully be
        removed.
      </p>

      <h2>14. Applicable law and consumer protections</h2>
      <p>
        These Terms do not choose a governing law in advance. The law applicable
        to these Terms and to any dispute is determined by the conflict-of-laws
        rules that apply in the relevant forum.
      </p>
      <p>
        Nothing in these Terms deprives a consumer of mandatory protections or
        access to courts that cannot lawfully be waived under the law applicable
        to that consumer.
      </p>

      <h2>15. General provisions</h2>

      <h3>15.1 Severability</h3>
      <p>
        If any provision of these Terms is found invalid or unenforceable, the
        remaining provisions will remain effective to the extent permitted by
        law.
      </p>

      <h3>15.2 No waiver</h3>
      <p>
        Failure to enforce a provision of these Terms does not constitute a
        waiver of that provision or any other right.
      </p>

      <h3>15.3 Assignment</h3>
      <p>
        You may not transfer your rights or obligations under these Terms
        without our consent, except where such restriction is prohibited by law.
      </p>
      <p>
        The Operator may transfer rights and obligations in connection with a
        transfer of the Service or its operation, subject to applicable law.
      </p>

      <h3>15.4 Entire agreement</h3>
      <p>
        These Terms and the applicable Privacy Policy constitute the entire
        agreement governing your use of the Service.
      </p>

      <h3>15.5 Language</h3>
      <p>
        If these Terms are translated, the English version will govern to the
        extent permitted by applicable law. Mandatory local-language
        requirements remain unaffected.
      </p>

      <h2>16. Contact</h2>
      <p>
        Questions about these Terms? Email{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </LegalPage>
  );
}
